import { apiClient } from '@/shared/lib/apiClient';
import type { ApiSuccessResponse } from '@super-admin/shared-types';

export type PortalAddonSlug = 'ai_tokens_200' | 'ai_tokens_500' | 'ai_tokens_1000';
export type PortalAddonPaymentProvider = 'myfatoorah' | 'instapay';

export type PortalAddonPreviewLine = {
  lineType: 'plan' | 'addon';
  slug: string;
  quantity: number;
  unitAmountMinor: number;
  originalUnitAmountMinor: number | null;
  discountPercent: number | null;
  lineTotalMinor: number;
};

export type PortalAddonPreviewResponse = {
  addon_slug: PortalAddonSlug;
  quote_id: string;
  expires_at: string;
  currency: string;
  billing_period: string;
  lines: PortalAddonPreviewLine[];
  subtotal_minor: number;
  discount_minor: number;
  total_minor: number;
  subscription_id: string;
};

export async function fetchPortalAddonPreview(
  addonSlug: PortalAddonSlug,
  quantity = 1,
): Promise<PortalAddonPreviewResponse> {
  const res = await apiClient.get<ApiSuccessResponse<PortalAddonPreviewResponse>>(
    '/api/v1/customer/subscription/addon-preview',
    { params: { addonSlug, quantity: String(quantity) } },
  );
  return res.data;
}

export type StartPortalAddonPurchaseInput = {
  addonSlug: PortalAddonSlug;
  quantity?: number;
  paymentProvider: PortalAddonPaymentProvider;
  locale?: 'ar' | 'en';
  idempotencyKey: string;
};

export type PortalAddonPurchaseResponse =
  | {
      payment_provider: 'myfatoorah';
      payment_id: string;
      payment_url: string;
      status: 'pending';
      amount_minor: number;
      currency: string;
      quote_id: string;
      checkout_session_id: string;
      addon_slug: PortalAddonSlug;
    }
  | {
      payment_provider: 'instapay';
      payment_id: string;
      status: 'pending_review';
      review_status: 'pending_review';
      amount_minor: number;
      currency: string;
      quote_id: string;
      addon_slug: PortalAddonSlug;
      instapay_details: {
        recipient_name?: string;
        recipient_phone?: string;
        recipient_iban?: string;
      };
    };

export async function startPortalAddonPurchase(
  input: StartPortalAddonPurchaseInput,
): Promise<PortalAddonPurchaseResponse> {
  const res = await apiClient.post<ApiSuccessResponse<PortalAddonPurchaseResponse>>(
    '/api/v1/customer/subscription/addon-purchase',
    {
      addonSlug: input.addonSlug,
      quantity: input.quantity,
      paymentProvider: input.paymentProvider,
      locale: input.locale,
    },
    {
      headers: { 'Idempotency-Key': input.idempotencyKey },
    },
  );
  return res.data;
}

export async function uploadPortalAddonPurchaseInstapayProof(
  paymentId: string,
  file: File,
): Promise<void> {
  const form = new FormData();
  form.append('receipt', file);
  await apiClient.postForm<ApiSuccessResponse<void>>(
    `/api/v1/customer/subscription/addon-purchase/${paymentId}/instapay-proof`,
    form,
    {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    },
  );
}
